import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, Briefcase, Check } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useSEO } from "@/hooks/useSEO";
import { Button } from "@/components/ui/button";
import { INDUSTRIES } from "@/lib/industriesData";
import {
  organizationLd,
  professionalServiceLd,
  webPageLd,
  breadcrumbLd,
  servicePackageLd,
} from "@/lib/structuredData";
import { FAQ } from "@/lib/seoContent";

const IndustryWebDesign = () => {
  const { industry } = useParams();
  const { t, lang, formatPrice } = useApp();
  const data = INDUSTRIES[industry];
  const c = data ? data[lang] || data.en : null;

  useSEO({
    title: c?.title,
    description: c?.metaDesc,
    path: `/website-development-for/${industry}`,
    lang,
    keywords: data
      ? [
          `website development for ${data.en.name}`,
          `web design for ${data.en.name}`,
          `Website erstellen lassen für ${data.de.name}`,
          `Webdesign für ${data.de.name}`,
          `günstige Website ${data.de.name}`,
          `affordable ${data.en.name} website`,
          `${data.en.name} web developer`,
          "NetoVibe",
        ]
      : [],
    jsonLd: data
      ? [
          organizationLd(),
          professionalServiceLd(),
          webPageLd({
            path: `/website-development-for/${industry}`,
            name: c.title,
            description: c.metaDesc,
            lang,
          }),
          breadcrumbLd([
            { name: t.nav.home, path: "/" },
            {
              name: lang === "de" ? "Branchen" : "Industries",
              path: "/website-development-for",
            },
            { name: c.name, path: `/website-development-for/${industry}` },
          ]),
        ]
      : [],
  });

  if (!data) return <Navigate to="/" replace />;

  return (
    <main data-testid={`industry-page-${industry}`} className="pt-16 lg:pt-20">
      <section>
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 lg:pt-28 pb-16 lg:pb-24">
          <div className="flex items-center gap-3 text-xs tracking-[0.2em] uppercase font-bold text-[#0A0A0A]">
            <Briefcase className="h-3.5 w-3.5 text-[#3A00FF]" aria-hidden />
            {lang === "de" ? "Branche" : "Industry"} · {c.name}
          </div>
          <h1
            data-testid={`industry-title-${industry}`}
            className="mt-6 text-4xl sm:text-5xl md:text-6xl lg:text-[6rem] leading-[0.92] tracking-tighter font-black text-[#0A0A0A] font-[Cabinet_Grotesk]"
          >
            {c.h1}
          </h1>
          <p className="mt-8 max-w-2xl text-base sm:text-lg text-[#525252] leading-relaxed">
            {c.lede}
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/contact">
              <Button className="h-12 px-6 rounded-none bg-[#0A0A0A] text-white hover:bg-[#3A00FF] text-xs tracking-widest uppercase font-bold shadow-[4px_4px_0px_#3A00FF] hover:shadow-[6px_6px_0px_#3A00FF] transition-all">
                {t.nav.cta}
                <ArrowUpRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/affordable-website-development">
              <Button
                variant="ghost"
                className="h-12 px-6 rounded-none border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white text-xs tracking-widest uppercase font-bold"
              >
                {lang === "de" ? "Alle Pakete" : "See packages"}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="border-t border-[#0A0A0A] bg-[#FAFAFA]">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <h2 className="text-3xl sm:text-4xl leading-tight tracking-tight font-black text-[#0A0A0A] font-[Cabinet_Grotesk]">
              {lang === "de"
                ? `Was wir speziell für ${c.name} lösen`
                : `What we specifically solve for ${c.name}`}
            </h2>
          </div>
          <ul className="lg:col-span-7 space-y-4">
            {c.benefits.map((b, i) => (
              <li
                key={i}
                data-testid={`industry-benefit-${i}`}
                className="flex items-start gap-3 border-b border-[#0A0A0A]/15 pb-4 text-base text-[#0A0A0A]"
              >
                <Check className="h-4 w-4 mt-1 text-[#3A00FF] shrink-0" aria-hidden />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {c.caseStudy && (
        <section className="border-t border-[#0A0A0A] bg-white">
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16 lg:py-20">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#3A00FF]">
              {lang === "de" ? "Referenz" : "Case study"}
            </div>
            <p className="mt-4 max-w-3xl text-2xl lg:text-3xl leading-snug tracking-tight text-[#0A0A0A] font-[Cabinet_Grotesk]">
              {c.caseStudy}
            </p>
            <Link
              to="/portfolio"
              className="mt-6 inline-flex items-center gap-2 text-xs tracking-widest uppercase font-bold text-[#0A0A0A] hover:text-[#3A00FF]"
            >
              {lang === "de" ? "Case Study öffnen" : "Read the case study"}{" "}
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      )}

      <section className="border-t border-[#0A0A0A] bg-white">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight font-black text-[#0A0A0A] font-[Cabinet_Grotesk] max-w-3xl">
            {lang === "de"
              ? `Preise für Ihre ${c.name}-Website`
              : `Pricing for your ${c.name} website`}
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 border-t border-l border-[#0A0A0A]">
            {t.pricing.packages.map((p) => (
              <article
                key={p.id}
                className="border-r border-b border-[#0A0A0A] p-8 bg-white flex flex-col"
              >
                <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#3A00FF]">
                  {p.id}
                </div>
                <h3 className="mt-3 text-xl tracking-tight font-black font-[Cabinet_Grotesk]">
                  {p.name}
                </h3>
                <div className="mt-6 text-4xl font-black tracking-tighter font-[Cabinet_Grotesk]">
                  {formatPrice(p)}
                </div>
                <p className="mt-3 text-sm text-[#525252] flex-1">{p.tagline}</p>
                <Link
                  to="/contact"
                  className="mt-6 inline-flex items-center gap-2 text-xs tracking-widest uppercase font-bold text-[#0A0A0A] hover:text-[#3A00FF]"
                >
                  {t.pricing.cta} <ArrowUpRight className="h-4 w-4" />
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-[#0A0A0A] bg-[#0A0A0A] text-white">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <h2 className="text-4xl sm:text-5xl leading-[0.95] tracking-tighter font-black font-[Cabinet_Grotesk]">
              {lang === "de"
                ? `Bereit für Ihre neue ${c.name}-Website?`
                : `Ready for a website built for ${c.name}?`}
            </h2>
          </div>
          <div className="lg:col-span-4 lg:justify-self-end">
            <Link to="/contact">
              <Button className="h-14 px-8 rounded-none bg-white text-[#0A0A0A] hover:bg-[#3A00FF] hover:text-white text-xs tracking-widest uppercase font-bold">
                {t.nav.cta}
                <ArrowUpRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default IndustryWebDesign;
